import { useEffect, useRef, useState } from "react";
import { getCustomisationValue, getGroupData } from "../../EditablePreview";
import InlineProgressBar, { convertCustomisationDataToBarSettings } from "../progress-bar/inline-progress-bar";
import VisualisedNode from "../VisualisedNode";
import './LivePoll.scss';
import { COLOUR_NAME_TO_COLOUR_HEX } from "../../../../../../../helpers/Colours";

const POLL_DUMMY_OPTIONS = [
    {hashtag: '#A', text: 'Option 1', perc: 60, points: 1},
    {hashtag: '#B', text: 'Option 2', perc: 2, points: 10},
    {hashtag: '#C', text: 'Option 3', perc: 30, points: 2},
    {hashtag: '#D', text: 'Option 4', perc: 50, points: 5},
    {hashtag: '#E', text: 'Option 5', perc: 10, points: 20},
    {hashtag: '#F', text: 'Option 6', perc: 40, points: 1},
    {hashtag: '#G', text: 'Option 7', perc: 5, points: 15},
    {hashtag: '#H', text: 'Option 8', perc: 8, points: 8},
    {hashtag: '#I', text: 'Option 9', perc: 20, points: 3},
    {hashtag: '#J', text: 'Option 10', perc: 30, points: 4},
    {hashtag: '#K', text: 'Option 11', perc: 90, points: 7},
    {hashtag: '#L', text: 'Option 12', perc: 70, points: 2},
    {hashtag: '#M', text: 'Option 13', perc: 12, points: 12},
    {hashtag: '#N', text: 'Option 14', perc: 60, points: 6},
    {hashtag: '#O', text: 'Option 15', perc: 70, points: 9},
];

const LivePoll = (props) => {
    console.log('LivePoll props', props);
    const previewScale = props.previewScale || 1;
    const campaignData = props.campaignData;
    // The top node here is the nain container - we can let VisualisedNode handle all of this bit, 
    // we are just going to set the height style to fit-content (polls don't have a dimensions / height configured and are always dynamic)
    // We are then going to control the chrildren of this node...
    const percBarSettings = convertCustomisationDataToBarSettings(getGroupData(props.node.subNodes[3].subNodes[3].customisationVars, 'hud.polls.percentageBarStyle'));
    percBarSettings.shapeAndPosition.shape[0].shape = 'square';
    const percBarWidth = 
       (getCustomisationValue(props.node.subNodes[3].subNodes[3].customisationVars, 'hud.polls.shapeSettings.panelWidth', 0, 'livePoll') 
        - getCustomisationValue(props.node.subNodes[3].subNodes[3].customisationVars, 'hud.polls.answerBoxStyle.hashtagAreaWidth', 0, 'livePoll') 
        - getCustomisationValue(props.node.subNodes[3].subNodes[3].customisationVars, 'hud.polls.answerBoxStyle.hashtagAreaSpacing', 0, 'livePoll')
        - 60)
        * previewScale;
    const hashtagAreaGap = getCustomisationValue(props.node.subNodes[3].subNodes[3].customisationVars, 'hud.polls.answerBoxStyle.hashtagAreaSpacing', 0, 'livePoll') * previewScale;

    console.log('LivePoll percBarWidth', percBarWidth, 'hashtagAreaGap', hashtagAreaGap);

    const [optionCountToRender, setOptionCountToRender] = useState(3);
    const optionsToRender_ref = useRef(-1);
    useEffect(() => {
        optionsToRender_ref.current = optionCountToRender;
    }, [optionCountToRender]);
    const renderMoreOptions = () => {
        setOptionCountToRender(prevCount => {
            if (prevCount < POLL_DUMMY_OPTIONS.length) {
                props.invalidateHighlightedNodeStates();
                return prevCount + 1;
            }
            return prevCount; // Don't exceed the number of options
        });
    }
    const renderLessOptions = () => {
        setOptionCountToRender(prevCount => {
            if (prevCount > 2) {
                props.invalidateHighlightedNodeStates();
                return prevCount - 1;
            }
            return prevCount; // Don't go below 2
        });
    }

    const showPollHeaderText = getCustomisationValue(props.node.subNodes[0].customisationVars, 'hud.polls.panelStyle.useHeaderText', 0, 'livePoll', props.node);
    const headerCopy = getCustomisationValue(props.node.subNodes[0].customisationVars, 'hud.polls.panelStyle.headertText', 0, 'livePoll', props.node) || 'POLL';
    const showPollHeaderImage = getCustomisationValue(props.node.subNodes[0].customisationVars, 'hud.polls.panelStyle.useImage', 0, 'livePoll', props.node);
    const headerImageWidthPerc = getCustomisationValue(props.node.subNodes[0].customisationVars, 'hud.polls.panelStyle.imageWidth', 0, 'livePoll', props.node);
    const headerImageAlignment = getCustomisationValue(props.node.subNodes[0].customisationVars, 'hud.polls.panelStyle.headerImageAlignment', 0, 'livePoll', props.node);
    const headerImageSrc = getCustomisationValue(props.node.subNodes[0].customisationVars, 'hud.polls.panelStyle.filename', 0, 'livePoll', props.node).srcUrl || null;

    const secretPoll = getCustomisationValue(props.node.customisationVars, 'hud.polls.shapeSettings.onlyShowPercAtEnd', 0, 'livePoll', props.node);
    const autoHighlightWinner = getCustomisationValue(props.node.customisationVars, 'hud.polls.shapeSettings.fadeLosersAtEnd', 0, 'livePoll', props.node);
    const fadeWhileAwaitingOutcome = getCustomisationValue(props.node.customisationVars, 'hud.polls.shapeSettings.fadeOptionWhileAwaitingOutcome', 0, 'livePoll', props.node);
    const autoFadeOptionsToRemove = getCustomisationValue(props.node.customisationVars, 'hud.polls.shapeSettings.fadeOptionsToBeRemoved', 0, 'livePoll', props.node);

    const hidePointsText = getCustomisationValue(props.node.subNodes[4].customisationVars, 'hud.polls.answerBoxStyle.hidePointsText', 0, 'livePoll', props.node);
    const [correctPredictionIndex, setCorrectPredictionIndex] = useState(-1);
    useEffect(() => {
        // Randomly set a correct prediction index when the poll is first rendered
        setCorrectPredictionIndex(Math.floor(Math.random() * Math.max(0, optionCountToRender - 1)));
    }, [optionCountToRender, props.timelinePlayheadStep]);
    const correctAnswerBgColor = COLOUR_NAME_TO_COLOUR_HEX(getCustomisationValue(props.node.subNodes[3].subNodes[3].customisationVars, 'hud.polls.answerBoxStyle.correctBackgroundColour', 0, 'livePoll', props.node), campaignData);
    const pointsUnit = getCustomisationValue(props.node.subNodes[3].subNodes[2].customisationVars, 'hud.polls.answerBoxStyle.pointsUnitCopy', 0, 'livePoll', props.node) || 'pts';

    const showPollPercs = !secretPoll || props.timelinePlayheadStep > 1;
    let opinionResult = -1;
    let perc = -1;
    for (let i = 0; i < POLL_DUMMY_OPTIONS.length; i++) {
        if (i >= (optionsToRender_ref.current || optionCountToRender)) break;
        if (POLL_DUMMY_OPTIONS[i].perc > perc) {
            opinionResult = i;
            perc = POLL_DUMMY_OPTIONS[i].perc;
            break;
        }
    }
    let removalResult = -1;
    perc = 101;
    for (let i = 0; i < POLL_DUMMY_OPTIONS.length; i++) {
        if (i >= (optionsToRender_ref.current || optionCountToRender)) break;
        if (POLL_DUMMY_OPTIONS[i].perc < perc) {
            removalResult = i;
            perc = POLL_DUMMY_OPTIONS[i].perc;
        }
    }
    

    // Top level renderers need to be wrapped in a VisualisedNode, so we can pass the props and style
    // Top level means the key in CustomComponentsList is the id of the feature / disruptor
    return (
        <VisualisedNode
            {...props}
            topLevel={true}
            style={{height: 'fit-content', margin: 45 * previewScale}}
        >
            {/* The first sub node is the poll header */}
            {(showPollHeaderText || showPollHeaderImage) &&
                <VisualisedNode
                    {...props}
                    node={props.node.subNodes[0]}
                >
                    {showPollHeaderImage && headerImageSrc &&
                        <>
                            <VisualisedNode 
                                {...props}
                                node={props.node.subNodes[0].subNodes[0]}
                                style={{
                                    width: headerImageWidthPerc ? `${headerImageWidthPerc}%` : 'auto',
                                    alignSelf: headerImageAlignment === 'left' ? 'flex-start' : (headerImageAlignment === 'right' ? 'flex-end' : 'center'),
                                }}
                                // style={{padding: `${30 * previewScale}px ${30 * previewScale}px`}}
                                // contentNode={true}
                                // fixedContent={'hud.polls.panelStyle.headertText'}
                            />
                            {/* <img
                                src={headerImageSrc}
                                alt="Poll Header"
                                style={{
                                    width: headerImageWidthPerc ? `${headerImageWidthPerc}%` : 'auto',
                                    alignSelf: headerImageAlignment === 'left' ? 'flex-start' : (headerImageAlignment === 'right' ? 'flex-end' : 'center'),
                                }}
                            /> */}
                        </>
                    }
                    {/* Using a VisualisedNode for the copy and setting fixedContent means our VisualisedNode component will handle editing text in place */}
                    {showPollHeaderText &&
                        <VisualisedNode 
                            {...props}
                            node={props.node.subNodes[0].subNodes[1]}
                            style={{padding: `${30 * previewScale}px ${30 * previewScale}px`}}
                            // contentNode={true}
                            // fixedContent={'hud.polls.panelStyle.headertText'}
                        />
                    }
                </VisualisedNode>
            }
            {/* Question text is next, but at this point we want a flex column with 30px gap */}
            <div className="v2-live-poll-content" style={{gap: `${30 * previewScale}px`}}>
                <VisualisedNode
                    {...props}
                    node={props.node.subNodes[1]}
                    style={{padding: `0px ${30 * previewScale}px 0px`, marginTop: `${30 * previewScale}px`}}
                >
                    Question Text
                </VisualisedNode>
                {/* Prompt text next, we want to control the padding*/}
                <VisualisedNode
                    {...props}
                    node={props.node.subNodes[2]}
                    style={{padding: `0 ${30 * previewScale}px`}}
                />
                {/* Now we are going to add in 4 example options */}
                <div className="v2-live-poll-content__options-container" style={{padding: `0 ${30 * previewScale}px`, gap: `${10 * previewScale}px`}}>
                    {POLL_DUMMY_OPTIONS.map((option, index) => {
                        if (index >= optionCountToRender) {
                            return null; // Don't render more than the set count
                        } else 
                        return (
                            <VisualisedNode
                                key={index}
                                {...props}
                                node={props.node.subNodes[3]}
                                style={
                                    {
                                        width: '100%',
                                        opacity: (fadeWhileAwaitingOutcome && props.timelinePlayheadStep === 3) ? 0.5 : 
                                                 (autoHighlightWinner && props.timelinePlayheadStep === 2 && index !== opinionResult) ? 0.5 : 
                                                 (autoFadeOptionsToRemove && props.timelinePlayheadStep === 5 && index === removalResult) ? 0.5 : 1,
                                    }
                                }
                                clickable={index === 0}
                            >
                                <>
                                    {/* The option box consists of 4 sub nodes: hashtag area, answer text, percentage text and percentage bar (in that order) */}
                                    <VisualisedNode
                                        {...props}
                                        node={props.node.subNodes[3].subNodes[0]}
                                        style={{padding: `0 ${20 * previewScale}px`, justifyContent: 'left', alignItems: 'center', minWidth: 'fit-content'}}
                                        clickable={index === 0}
                                    >
                                        {option.hashtag}
                                    </VisualisedNode>
                                    <div className="v2-live-poll-content__option-answer-area" 
                                        style={
                                            {
                                                width: percBarWidth, 
                                                marginLeft: hashtagAreaGap,
                                                backgroundColor: (props.timelinePlayheadStep !== 4 || index !== correctPredictionIndex) ? 'transparent' : correctAnswerBgColor,
                                            }
                                        }
                                    >
                                        <div className="v2-live-poll-content__option-perc-bar" style={{width: percBarWidth}}>
                                            <VisualisedNode
                                                {...props}
                                                node={props.node.subNodes[3].subNodes[4]}
                                                style={{width: '100%', height: '100%'}}
                                                clickable={index === 0}
                                            >
                                                {/* The percentage bar is a sub node of the option node, we can just render it */}
                                            {(props.timelinePlayheadStep !== 4 || index !== correctPredictionIndex) &&
                                                <InlineProgressBar
                                                    viewId={`live-poll-perc-${props.node.id}-${index}`}
                                                    customSettings={percBarSettings}
                                                    fillPerc={showPollPercs ? option.perc / 100 : 0}
                                                    useOutline={false}
                                                    width={percBarWidth}
                                                    height={getCustomisationValue(props.node.subNodes[3].subNodes[4].customisationVars, 'hud.polls.answerBoxStyle.height') * props.previewScale}
                                                />
                                            }
                                            </VisualisedNode>
                                        </div>
                                        <div className="v2-live-poll-content__option-perc-bar" style={{width: percBarWidth}}>

                                            {props.timelinePlayheadStep === 4 && index === correctPredictionIndex &&
                                                <VisualisedNode
                                                    {...props}
                                                    node={props.node.subNodes[3].subNodes[5]}
                                                    style={{width: '100%', height: '100%'}}
                                                >
                                                </VisualisedNode>
                                            }
                                        </div>

                                        <div className="v2-live-poll-content__option-over-bar-content" style={{padding: `0 ${20 * previewScale}px`}}>
                                            <div className=".v2-live-poll-content__option-answer-points-column">
                                                <VisualisedNode
                                                    {...props}
                                                    style = {{width: 'fit-content'}}
                                                    node={props.node.subNodes[3].subNodes[1]}
                                                    clickable={index === 0}
                                                >
                                                    {option.text}
                                                </VisualisedNode>
                                                {!hidePointsText &&
                                                    <VisualisedNode
                                                        {...props}
                                                        style = {{width: 'fit-content'}}
                                                        node={props.node.subNodes[3].subNodes[2]}
                                                        clickable={index === 0}
                                                    >{option.points}{pointsUnit}</VisualisedNode>
                                                }
                                            </div>
                                            <div className="grow"
                                                style={{height: '100%'}}
                                                onClick={
                                                    (!props.editingTextContentData && (index === 0 || (props.timelinePlayheadStep === 4 && index === correctPredictionIndex))) ?
                                                        (e) => {
                                                            e.stopPropagation();
                                                            e.preventDefault();
                                                            if (!props.draggingActive.current) {
                                                                props.draggingCancelled.current = true;
                                                                // Use this to select the percentage bar node, as we can't click straight through to it
                                                                props.activateNodeFunction(props.node.subNodes[3].subNodes[props.timelinePlayheadStep === 4 && index === correctPredictionIndex ? 5 : 4]);
                                                            }
                                                        }
                                                        : null
                                                }
                                                onMouseOverCapture={(index === 0 || (props.timelinePlayheadStep === 4 && index === correctPredictionIndex)) ? (e) => props.highlightHoveredNode(e, props.node.subNodes[3].subNodes[props.timelinePlayheadStep === 4 && index === correctPredictionIndex ? 5 : 4]) : null}
                                                onMouseLeave={(index === 0 || (props.timelinePlayheadStep === 4 && index === correctPredictionIndex)) ? (e) => props.unhighlightHoveredNode(e, props.node.subNodes[3].subNodes[props.timelinePlayheadStep === 4 && index === correctPredictionIndex ? 5 : 4]) : null}
                                            ></div>
                                            {showPollPercs && index < optionCountToRender - 1 && (props.timelinePlayheadStep !== 4 || index !== correctPredictionIndex) &&
                                                <VisualisedNode
                                                    {...props}
                                                    style = {{width: 'fit-content'}}
                                                    node={props.node.subNodes[3].subNodes[3]}
                                                    clickable={index === 0}
                                                >
                                                    {option.perc}%
                                                </VisualisedNode>
                                            }
                                            {props.timelinePlayheadStep === 4 && index === correctPredictionIndex &&
                                                <VisualisedNode
                                                    {...props}
                                                    style = {{width: 'fit-content'}}
                                                    node={props.node.subNodes[3].subNodes[5]}
                                                >
                                                    Correct!
                                                </VisualisedNode>
                                            }
                                            {index === optionCountToRender - 1 &&
                                                <>
                                                    <div className="v2-live-poll-options-up-down-button" onClick={renderLessOptions}>-</div>
                                                    <div className="v2-live-poll-options-up-down-button" onClick={renderMoreOptions}>+</div>
                                                </>
                                            }
                                        </div>
                                    </div>
                                </>
                            </VisualisedNode>
                        )
                    })}
                </div>
                {/* Finally, we have the poll footer */}
                <VisualisedNode
                    {...props}
                    node={props.node.subNodes[4]}
                    style={
                        {
                            padding: `${30 * previewScale}px`,
                            position: 'relative',
                            whiteSpace: 'nowrap', 
                            overflow: 'hidden',
                        }
                    }
                    contentStyle={{
                        height: `${getCustomisationValue(props.node.subNodes[4].customisationVars, 'hud.polls.timerBarStyle.timerBarThickness') * previewScale}px`,
                        overflow: 'visible',
                        position: 'relative',
                    }}
                    innerContentStyle={{
                        position: 'absolute',
                        top: '50%',
                        transform: 'translateY(-50%)',
                    }}
                >
                    {/* Check if this is a version of the footer than includes a progress bar */}
                    {/*props.node.subNodes[4].subNodes.length > 0 && props.node.subNodes[4].subNodes[0]?.name?.toLowerCase()?.includes(' bar') &&*/
                        <>
                            <VisualisedNode
                                {...props}
                                node={props.node.subNodes[4].subNodes[0]}
                                style={{width: '100%'}}
                            >
                                {/* The footer has a sub node for the poll timer, we can just render that */}
                                <InlineProgressBar
                                    viewId={`live-poll-timer-${props.node.id}`}
                                    customSettings={convertCustomisationDataToBarSettings(getGroupData(props.node.subNodes[4].subNodes[0].customisationVars, 'hud.polls.timerBarStyle'))}
                                    fillPerc={0.7}
                                    useOutline={false}
                                    width={
                                        ((getCustomisationValue(props.node.subNodes[4].subNodes[0].customisationVars, 'hud.polls.shapeSettings.panelWidth'))
                                        - (getCustomisationValue(props.node.subNodes[4].subNodes[0].customisationVars, 'hud.polls.shapeSettings.borderThickness') * 2)
                                        - 60) * props.previewScale
                                    }
                                    height={getCustomisationValue(props.node.subNodes[4].subNodes[0].customisationVars, 'hud.polls.timerBarStyle.timerBarThickness') * props.previewScale}
                                />
                            </VisualisedNode>
                            <VisualisedNode
                                {...props}
                                node={props.node.subNodes[4].subNodes[1]}
                                style={{position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)'}}
                            >
                            </VisualisedNode>
                            <VisualisedNode
                                {...props}
                                node={props.node.subNodes[4].subNodes[2]}
                                style={{position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)'}}
                            >
                                <>- VOTING ENDED -</>
                            </VisualisedNode>
                            <VisualisedNode
                                {...props}
                                node={props.node.subNodes[4].subNodes[3]}
                                style={{position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)'}}
                            >
                                <>- AWAITING OUTCOME -</>
                            </VisualisedNode>
                            <VisualisedNode
                                {...props}
                                node={props.node.subNodes[4].subNodes[4]}
                                style={{position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)'}}
                            >
                                <>99999 PLAYERS GOT IT RIGHT!</>
                            </VisualisedNode>
                            <VisualisedNode
                                {...props}
                                node={props.node.subNodes[4].subNodes[5]}
                                style={{position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)'}}
                            >
                                <>- VOTING ENDED -</>
                            </VisualisedNode>
                        </>
                    }
                </VisualisedNode>
            </div>
        </VisualisedNode>
    )
}

export default LivePoll;
